import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import { isEmpty } from 'lodash'
import {
  getCRMFormData,
  type CrmAuthFormData,
} from '../../modules/crmAuth/actions'
import { LoadingTypes } from '../../modules/loading/reducer'
import { CRMTypes } from '../constants'
import { isLoading as Loading } from '../loadingState'
import type { ComponentLoadingState } from '../../modules/loading/reducer'

export type FlyOutOnboardingStatus = {
  hasCompanyInfo: boolean
  hasConnectedCrmAtLeastOnce: boolean
}

type FlyOutOnboardingStatusState = {
  AccountData: {
    user: {
      partnerData?: {
        crmId?: string
        companyName?: string
        websiteUrl?: string
        industry?: string
      } | null
    }
  }
  flyoutSellerInfo?: {
    onboarded?: boolean
  }
  flyOutSellerInfoMicrosoft?: {
    onboarded?: boolean
  }
  flyOutOnboarding: {
    microsoftOnboardingForm?: {
      filledCompanyInfo?: boolean
      filledCloudSettings?: boolean
    }
    onboardingForm?: {
      pageMarker?: {
        page1?: boolean
        page2?: boolean
      }
    }
  }
  CRMAuth: {
    crmFormData?: CrmAuthFormData | null
  }
  loading: ComponentLoadingState
}

const PAGE_MARKER = 'pageMarker'

export const useFlyOutOnboardingStatus = () => {
  const dispatch =
    useDispatch<
      ThunkDispatch<FlyOutOnboardingStatusState, unknown, AnyAction>
    >()
  const crmId = useSelector<FlyOutOnboardingStatusState, string | undefined>(
    state => state?.AccountData.user.partnerData?.crmId
  )
  const awsSellerInfo = useSelector<
    FlyOutOnboardingStatusState,
    { flyoutSellerInfo?: { onboarded?: boolean } }
  >(state => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
  }))

  const microsoftSellerInfo = useSelector<
    FlyOutOnboardingStatusState,
    { flyOutSellerInfoMicrosoft?: { onboarded?: boolean } }
  >(state => ({
    flyOutSellerInfoMicrosoft: state.flyOutSellerInfoMicrosoft,
  }))

  const microsoftOnboardingValues = useSelector(
    (state: FlyOutOnboardingStatusState) =>
      state.flyOutOnboarding.microsoftOnboardingForm
  )
  const awsOnboardingValues = useSelector(
    (state: FlyOutOnboardingStatusState) =>
      state.flyOutOnboarding.onboardingForm
  )

  const { deletedAt, crmIdFromCRMAuth, crmAuth } = useSelector(
    (state: FlyOutOnboardingStatusState) => {
      const isCrmConnected =
        !isEmpty(state.CRMAuth.crmFormData) &&
        !isEmpty(crmId) &&
        (state.CRMAuth.crmFormData?.creds || []).length > 0 &&
        isEmpty(state.CRMAuth.crmFormData?.deleted_at)
      return {
        deletedAt: state.CRMAuth.crmFormData?.deleted_at || '',
        crmIdFromCRMAuth: state.CRMAuth.crmFormData?.crm_id || '',
        crmType: state.CRMAuth.crmFormData?.crm_type as CRMTypes,
        crmAuth: state.CRMAuth.crmFormData
          ? state.CRMAuth.crmFormData
          : undefined,
        isCrmConnected,
        isLoading: Loading(state.loading)(LoadingTypes.CRM_AUTH),
      }
    }
  )

  const hasCompanyInfo = useSelector((state: FlyOutOnboardingStatusState) => {
    const partnerData = state.AccountData.user?.partnerData
    return (
      !isEmpty(partnerData?.companyName) &&
      !isEmpty(partnerData?.websiteUrl) &&
      !isEmpty(partnerData?.industry)
    )
  })

  const hasConnectedCrmAtLeastOnce = useSelector(
    (state: FlyOutOnboardingStatusState) => {
      return (
        !isEmpty(state.CRMAuth.crmFormData) &&
        !isEmpty(state.AccountData.user?.partnerData?.crmId) &&
        (state.CRMAuth.crmFormData?.creds || []).length > 0 &&
        isEmpty(state.CRMAuth.crmFormData?.deleted_at)
      )
    }
  )

  const _isNewCompanyInfoComplete = microsoftOnboardingValues?.filledCompanyInfo

  const _isOldAwsCompanyInfoComplete = awsOnboardingValues?.[PAGE_MARKER]?.page1

  const isCompanyInfoComplete =
    _isNewCompanyInfoComplete || _isOldAwsCompanyInfoComplete

  const _isAwsCloudSettingsComplete = awsOnboardingValues?.[PAGE_MARKER]?.page2

  const _isMicrosoftCloudSettingsComplete =
    microsoftOnboardingValues?.filledCloudSettings

  const isCloudSettingsComplete =
    _isAwsCloudSettingsComplete || _isMicrosoftCloudSettingsComplete

  const isAwsOnboardingComplete =
    awsSellerInfo?.flyoutSellerInfo?.onboarded === true

  const isMicrosoftOnboardingComplete =
    microsoftSellerInfo?.flyOutSellerInfoMicrosoft?.onboarded === true

  const onboardingComplete =
    isAwsOnboardingComplete || isMicrosoftOnboardingComplete

  useEffect(() => {
    if (crmId && isEmpty(crmAuth) && !isEmpty(crmId)) {
      dispatch(getCRMFormData(crmId, { errorPresentation: 'none' }))
    }
  }, [crmIdFromCRMAuth, crmAuth, crmId, deletedAt])

  const result = {
    onboardingComplete,
    _isNewCompanyInfoComplete,
    _isOldAwsCompanyInfoComplete,
    _isAwsCloudSettingsComplete,
    _isMicrosoftCloudSettingsComplete,
    isCompanyInfoComplete,
    isCloudSettingsComplete,
    hasCompanyInfo,
    hasConnectedCrmAtLeastOnce,
  }
  return result
}
